import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Backdrop } from "@/components/eclipsia/Backdrop";
import { BlurText } from "@/components/eclipsia/BlurText";
import { Logo } from "@/components/eclipsia/Logo";
import { SiteFooter } from "@/components/eclipsia/SiteFooter";
import { HalloweenAccents, HalloweenGlow } from "@/components/eclipsia/HalloweenAccents";
import { Countdown } from "@/components/eclipsia/Countdown";
import { useConfig } from "@/hooks/useConfig";
import { formatDateIt, SITE } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];
const rise = (delay, y = 20) => ({
  initial: { filter: "blur(10px)", opacity: 0, y },
  animate: { filter: "blur(0px)", opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: EASE, delay },
});

const MotionLink = motion.create(Link);

export default function Home() {
  const { config } = useConfig();
  const date = formatDateIt(config.eventDate);
  const t = config.texts;

  return (
    <main className="relative min-h-screen w-full overflow-hidden bg-black flex flex-col" data-testid="home-page">
      <Backdrop video={config.heroVideo} />
      <div className="relative z-10 flex flex-col flex-1 min-h-screen">
        <Logo />
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center py-10">
          <motion.p {...rise(0.2, -10)} data-testid="hero-eyebrow"
            className="text-[11px] sm:text-xs tracking-[0.3em] uppercase text-white/60 font-body font-medium mb-4">
            {t.homeEyebrow}
          </motion.p>
          <BlurText
            key={t.homeHeadline}
            testId="hero-headline"
            text={t.homeHeadline}
            className="font-heading italic font-normal text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] max-w-3xl tracking-[-2px]"
          />
          <div className="relative mt-12">
            {config.halloween && <HalloweenGlow />}
            {config.halloween && <HalloweenAccents />}
            <MotionLink to="/prenota" {...rise(1.6)} data-testid="next-event-cta"
              className="glass-strong rounded-[1.75rem] px-10 py-5 flex flex-col items-center gap-1 group relative">
              <span className="text-[11px] tracking-[0.3em] uppercase text-white/70 font-body font-medium flex items-center gap-1.5">
                {t.ctaLabel}
                <ArrowUpRight className="w-3 h-3 opacity-70 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
              <span data-testid="next-event-date" className="font-heading italic text-white text-5xl md:text-6xl tracking-[-1px] leading-none">
                {date.short}
              </span>
              <Countdown eventDate={config.eventDate} />
            </MotionLink>
          </div>
          <motion.div {...rise(1.9)} className="mt-6 flex flex-col items-center gap-1.5 font-body">
            <span className="uppercase tracking-[0.25em] text-white/50 text-[10px]">{t.tavoliLabel}</span>
            <span className="flex items-center gap-2 flex-wrap justify-center text-white/80 text-sm">
              {SITE.phones.map((p, i) => (
                <span key={p.href} className="flex items-center gap-2">
                  {i > 0 && <span className="text-white/30">·</span>}
                  <a href={p.href} data-testid={`phone-link-${i}`} className="hover:text-white transition-colors">{p.label}</a>
                </span>
              ))}
            </span>
          </motion.div>
        </div>
        <SiteFooter />
      </div>
    </main>
  );
}
